import { BarChart3, Bell, Boxes, Briefcase, CalendarDays, CalendarOff, FileText, Home, LifeBuoy, Megaphone, MessageSquare, MoreHorizontal, Package, Receipt, Settings, Sparkles, Store, Sun, Timer, UserCheck, Users } from "lucide-react";

// Information architecture — one place for the sidebar, the mobile nav, the More sheet and the hub sub-tabs.
// Primary workflow only at the top level; secondary features live under "More" so they never compete with daily work.

export const ADMIN_NAV = [
  { group: "Workspace", items: [
    { key: "home", label: "Home", to: "/", icon: Home, end: true },
    { key: "intelligence", label: "Intelligence", to: "/intelligence", icon: Sparkles, paths: ["/intelligence"] },
    { key: "crm", label: "CRM", to: "/deals/pipeline", icon: Users, paths: ["/deals", "/crm", "/customers", "/bookings"], exclude: ["/deals/quotes"], children: [
      { key: "leads", label: "Leads", to: "/deals/pipeline", hint: "open leads", count: "deals.pipeline" }, { key: "customers", label: "Customers", to: "/customers" }, { key: "deals", label: "Deals", to: "/deals", end: true, drill: "/deals?view=attention", tone: "urgent", hint: "deals needing attention", count: "deals.attention" }, { key: "bookings", label: "Bookings", to: "/deals/bookings" },
    ] },
    { key: "jobs", label: "Jobs", to: "/jobs", icon: Briefcase, paths: ["/jobs", "/projects"], children: [
      { key: "all", label: "Active jobs", to: "/jobs", end: true }, { key: "projects", label: "Projects", to: "/jobs/projects" }, { key: "variations", label: "Variations", to: "/jobs/variations", tone: "warn", drill: "/jobs/variations?status=sent", hint: "awaiting approval" },
    ] },
    { key: "schedule", label: "Schedule", to: "/dispatch/schedule", icon: CalendarDays, paths: ["/dispatch"], children: [
      { key: "schedule", label: "Schedule", to: "/dispatch/schedule" }, { key: "live", label: "Live map", to: "/dispatch/live" }, { key: "clockin", label: "Clock in", to: "/dispatch/clock-in" }, { key: "timesheets", label: "Timesheets", to: "/dispatch/timesheets", tone: "warn", drill: "/dispatch/timesheets?filter=pending", hint: "shifts to approve", count: "dispatch.timesheets" }, { key: "leave", label: "Leave", to: "/dispatch/leave", tone: "warn", drill: "/dispatch/leave?status=pending", hint: "requests awaiting decision", count: "dispatch.leave" },
    ] },
    { key: "quotes", label: "Quotes", to: "/deals/quotes", icon: FileText, paths: ["/deals/quotes", "/quotes"] },
    { key: "invoices", label: "Invoices", to: "/money/invoices", icon: Receipt, paths: ["/money", "/invoices"], children: [
      { key: "invoices", label: "Invoices", to: "/money/invoices", tone: "urgent", drill: "/money/invoices?status=overdue", hint: "overdue", count: "money.invoices" }, { key: "payments", label: "Payments", to: "/money/payments", count: "money.payments", hint: "received this month" }, { key: "profitability", label: "Profitability", to: "/money/profitability" }, { key: "overview", label: "Money overview", to: "/money", end: true },
    ] },
    { key: "supply", label: "Supply", to: "/supply", icon: Package, paths: ["/supply", "/supplies"], children: [
      { key: "overview", label: "Overview", to: "/supply", end: true }, { key: "orders", label: "Purchase orders", to: "/supply/purchase-orders", tone: "warn", drill: "/supply/purchase-orders?status=awaiting_approval", hint: "awaiting approval" }, { key: "deliveries", label: "Deliveries", to: "/supply/deliveries", tone: "urgent", drill: "/supply/deliveries?filter=late", hint: "late or delayed" },
      { key: "suppliers", label: "Suppliers", to: "/supply/suppliers" }, { key: "materials", label: "Materials", to: "/supply/materials", tone: "warn", drill: "/supply/materials?filter=requires_review", hint: "items needing review" }, { key: "requests", label: "Requests", to: "/supplies", end: true },
    ] },
    { key: "team", label: "Team", to: "/team", icon: UserCheck, paths: ["/team", "/subcontractors"], children: [
      { key: "employees", label: "Employees", to: "/team", end: true }, { key: "subcontractors", label: "Subcontractors", to: "/team/subcontractors", tone: "urgent", drill: "/team/subcontractors?filter=issues", hint: "compliance issues" }, { key: "availability", label: "Availability", to: "/team/availability" },
    ] },
    { key: "reports", label: "Reports", to: "/reports", icon: BarChart3 },
  ] },
  { group: "More", fold: true, icon: MoreHorizontal, items: [
    { key: "messages", label: "Messages", to: "/messages", icon: MessageSquare, badge: "messages" },
    { key: "marketplace", label: "Marketplace", to: "/marketplace", icon: Store },
    { key: "marketing", label: "Marketing", to: "/marketing/social", icon: Megaphone, paths: ["/marketing"], children: [
      { key: "social", label: "Social media", to: "/marketing/social" }, { key: "content", label: "Content", to: "/marketing/social?tab=create", tone: "warn", drill: "/marketing/social?tab=approve", hint: "posts needing approval" }, { key: "reviews", label: "Reviews", to: "/marketing/reviews", drill: "/marketing/reviews?status=scheduled", hint: "scheduled to send" },
    ] },
    { key: "integrations", label: "Integrations", to: "/integrations", icon: Boxes },
    { key: "settings", label: "Settings", to: "/settings", icon: Settings, paths: ["/settings"] },
    { key: "help", label: "Help", to: "/help", icon: LifeBuoy, paths: ["/help", "/training"] },
  ] },
];

export const FIELD_NAV = [
  { group: "Today", items: [
    { key: "my-day", label: "My Day", to: "/", icon: Sun, end: true },
    { key: "jobs", label: "Jobs", to: "/jobs", icon: Briefcase, paths: ["/jobs"] },
    { key: "schedule", label: "Schedule", to: "/dispatch/schedule", icon: CalendarDays },
    { key: "messages", label: "Messages", to: "/messages", icon: MessageSquare, badge: "messages" },
    { key: "clockin", label: "Clock In", to: "/dispatch/clock-in", icon: Timer },
    { key: "leave", label: "Leave", to: "/dispatch/leave", icon: CalendarOff },
    { key: "supplies", label: "Supplies", to: "/supplies", icon: Package },
  ] },
  { group: "You", items: [
    { key: "notifications", label: "Notifications", to: "/notifications", icon: Bell },
    { key: "profile", label: "Profile", to: "/settings", icon: Settings },
    { key: "help", label: "Help", to: "/help", icon: LifeBuoy },
  ] },
];

export const MOBILE_ADMIN = ["home", "crm", "jobs", "schedule"];
export const MOBILE_FIELD = ["my-day", "jobs", "clockin", "messages"];

const pathOf = (to) => to.split("?")[0];
const queryOf = (to) => new URLSearchParams(to.includes("?") ? to.slice(to.indexOf("?")) : "");
const startsAt = (pathname, base) => base === "/" ? pathname === "/" : pathname === base || pathname.startsWith(`${base}/`);

// Does a leaf link match the current location? Query params in the link must all be present.
export function linkMatches(item, loc) {
  const base = pathOf(item.to);
  const exact = item.end || base === "/";
  if (exact ? loc.pathname !== base : !startsAt(loc.pathname, base)) return false;
  const want = queryOf(item.to), have = new URLSearchParams(loc.search);
  for (const [k, v] of want) if (have.get(k) !== v) return false;
  return true;
}

// Among a hub's children pick the single best match: most query params satisfied wins, then longest path.
export function activeChild(item, loc) {
  const hits = (item.children || []).filter((c) => linkMatches(c, loc));
  if (!hits.length) return null;
  const q = (c) => [...queryOf(c.to)].length;
  return hits.sort((a, b) => q(b) - q(a) || pathOf(b.to).length - pathOf(a.to).length)[0];
}

const inPaths = (item, loc) => (item.paths || []).some((p) => startsAt(loc.pathname, p)) && !(item.exclude || []).some((p) => startsAt(loc.pathname, p));

export function itemActive(item, loc) {
  if (item.children) return !!activeChild(item, loc) || inPaths(item, loc);
  if (item.paths) return inPaths(item, loc);
  return linkMatches(item, loc);
}

// Hub whose sub-tab strip should render for this location (only exact hub tab routes, never detail pages).
export function hubFor(loc) {
  for (const g of ADMIN_NAV) for (const it of g.items) {
    if (!it.children || it.strip === false) continue;
    if (it.children.some((c) => pathOf(c.to) === loc.pathname)) return it;
  }
  return null;
}

// Old → new routes. Query strings are preserved; a few tabs move to their own path.
export const MOVED = {
  "/crm": "/deals/pipeline", "/pipeline": "/deals/pipeline", "/bookings": "/deals/bookings", "/quotes": "/deals/quotes",
  "/projects": "/jobs/projects", "/calendar": "/dispatch/schedule", "/schedule": "/dispatch/schedule", "/dispatch": "/dispatch/schedule",
  "/clockin": "/dispatch/clock-in", "/clock-in": "/dispatch/clock-in", "/timesheets": "/dispatch/timesheets", "/leave": "/dispatch/leave",
  "/subcontractors": "/team/subcontractors", "/invoices": "/money/invoices", "/payments": "/money/payments", "/profitability": "/money/profitability",
  "/social": "/marketing/social", "/social-media": "/marketing/social", "/marketing": "/marketing/social", "/reviews": "/marketing/reviews", "/variations": "/jobs/variations",
};

export function movedTarget(pathname, search) {
  const base = MOVED[pathname];
  if (!base) return null;
  const p = new URLSearchParams(search);
  const tab = p.get("tab");
  if (pathname === "/clockin" || pathname === "/clock-in") {
    if (tab === "timesheets") { p.delete("tab"); return withQs("/dispatch/timesheets", p); }
    if (tab === "map") { p.delete("tab"); return withQs("/dispatch/live", p); }
  }
  if (pathname === "/leave" && tab === "availability") { p.delete("tab"); return withQs("/team/availability", p); }
  if (pathname === "/quotes" && tab === "variations") { p.delete("tab"); return withQs("/jobs/variations", p); }
  return withQs(base, p);
}

const withQs = (path, p) => { const s = p.toString(); return s ? `${path}?${s}` : path; };
